import { getDefaultOrFirstLang, MultiLingual } from '@/models/language.model';
import React, { useState } from 'react';
import { Table, Button, IconButton, Stack, Tag, Message } from 'rsuite';
import EditIcon from '@rsuite/icons/Edit';
import TrashIcon from '@rsuite/icons/Trash';
import { AxiosError } from 'axios';
import PageContent from '@/components/PageContent';
import { BaseErrorModel } from '@/models/core.model';
import { EnergyHub, EnergyHubOffer, EnergyHubOfferType, EnergyHubOfferTypeList } from '@/models/energy-hub.model';
import { deleteEnergyHubOffer } from '@/services/energy-hub.service';
import { toastErrorMessage, toastSuccessMessage } from '@/utils/toasterMessage';
import { jsDateToZone } from '@/utils/date-time';
import OfferDrawer from './offer-drawer';

const { Column, HeaderCell, Cell } = Table;

/** Every language, e.g. "Free espresso / 免費濃縮咖啡". Text can be null when the field is empty. */
const allLanguages = (text?: MultiLingual | null) => Object.values(text ?? {}).filter(Boolean).join(' / ');

const isLive = (o: EnergyHubOffer) => {
  const now = Date.now();
  return o.isActive
    && (!o.validFrom || o.validFrom.getTime() <= now)
    && (!o.validTo || o.validTo.getTime() > now);
};

interface Props {
  hub: EnergyHub;
  onChanged: () => void;
}

const EnergyHubOfferList: React.FC<Props> = ({ hub, onChanged }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<EnergyHubOffer | undefined>(undefined);

  const openDrawer = (offer?: EnergyHubOffer) => {
    setEditing(offer);
    setDrawerOpen(true);
  };

  const handleDelete = async (row: EnergyHubOffer) => {
    if (!window.confirm(`Delete the offer "${getDefaultOrFirstLang(row.title ?? {})}"?`)) return;
    try {
      await deleteEnergyHubOffer(hub.id, row.id);
      toastSuccessMessage('Offer deleted');
      onChanged();
    } catch (err) {
      const em = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in deleting offer: ${em?.errorMessage}`);
    }
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={() => openDrawer()}>Add Offer</Button>
      </Stack>
      {!hub.offers.some(isLive) && (
        <Message type="warning" showIcon style={{ marginBottom: 16 }}>
          This hub has no live offer, so runners see nothing to claim. Add one, or check the active dates.
        </Message>
      )}
      <Table autoHeight data={hub.offers}>
        <Column flexGrow={0.4}>
          <HeaderCell>Id</HeaderCell>
          <Cell dataKey="id" />
        </Column>
        <Column flexGrow={0.9}>
          <HeaderCell>Type</HeaderCell>
          <Cell>
            {(row: EnergyHubOffer) => (
              <>
                {EnergyHubOfferTypeList.find(t => t.value == row.type)?.label ?? row.type}
                {row.type == EnergyHubOfferType.Discount && row.discountPercent != null && ` (${row.discountPercent}%)`}
              </>
            )}
          </Cell>
        </Column>
        <Column flexGrow={2} fullText>
          <HeaderCell>Title</HeaderCell>
          <Cell>{(row: EnergyHubOffer) => allLanguages(row.title)}</Cell>
        </Column>
        <Column flexGrow={2} fullText>
          <HeaderCell>Terms</HeaderCell>
          <Cell>{(row: EnergyHubOffer) => allLanguages(row.terms)}</Cell>
        </Column>
        <Column flexGrow={0.7} align="center">
          <HeaderCell>Live</HeaderCell>
          <Cell>
            {(row: EnergyHubOffer) => isLive(row)
              ? <Tag color="green">Live</Tag>
              : <Tag>{row.isActive ? 'Out of dates' : 'Inactive'}</Tag>}
          </Cell>
        </Column>
        <Column flexGrow={1.1}>
          <HeaderCell>Valid From</HeaderCell>
          <Cell fullText>{(row: EnergyHubOffer) => jsDateToZone(row.validFrom)}</Cell>
        </Column>
        <Column flexGrow={1.1}>
          <HeaderCell>Valid To</HeaderCell>
          <Cell fullText>{(row: EnergyHubOffer) => jsDateToZone(row.validTo)}</Cell>
        </Column>
        <Column width={100} fixed="right">
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(row: EnergyHubOffer) => (
              <Stack spacing={6}>
                <IconButton icon={<EditIcon />} circle size="sm" onClick={() => openDrawer(row)} />
                <IconButton icon={<TrashIcon />} circle size="sm" color="red" appearance="ghost" onClick={() => handleDelete(row)} />
              </Stack>
            )}
          </Cell>
        </Column>
      </Table>
      <OfferDrawer
        open={drawerOpen}
        energyHubId={hub.id}
        offer={editing}
        onClose={() => setDrawerOpen(false)}
        onSubmitted={() => { setDrawerOpen(false); onChanged(); }}
      />
    </PageContent>
  );
};

export default EnergyHubOfferList;
